import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import config from '../config/env';

interface RefreshTokenResponse {
  success: boolean;
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number;
  message?: string;
}

interface QueuedRequest {
  resolve: (value: any) => void;
  reject: (error: any) => void;
  config: AxiosRequestConfig;
}

/**
 * Token Refresh Service for Frontend
 * Handles automatic token refresh and request retry logic
 */
export class TokenRefreshService {
  private isRefreshing = false;
  private failedQueue: QueuedRequest[] = [];

  constructor() {
    // No automatic monitoring - only refresh on 401 errors
  }

  /**
   * Setup axios interceptor for automatic token refresh
   */
  public setupAxiosInterceptor(axiosInstance: AxiosInstance): void {
    // Response interceptor
    axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Handle timeout errors
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
          toast.error('リクエストがタイムアウトしました。しばらく待ってから再試行してください。');
          return Promise.reject({
            success: false,
            message: 'リクエストがタイムアウトしました。しばらく待ってから再試行してください。',
            errors: [{ field: 'general', message: 'ネットワーク接続が遅いか、サーバーが応答していません。' }],
            isTimeout: true
          });
        }

        // Only handle 401 errors and avoid infinite retry loops
        // Skip token refresh for login endpoint to avoid redirect loops
        if (error.response?.status === 401 && 
            !originalRequest?._retry && 
            !originalRequest?.url?.includes('/login')) {
          return this.handleTokenRefresh(axiosInstance, originalRequest);
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Handle token refresh logic
   */
  private async handleTokenRefresh(
    axiosInstance: AxiosInstance,
    originalRequest: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    // If already refreshing, queue the request
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject, config: originalRequest });
      }).then((token) => {
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return axiosInstance(originalRequest);
      }).catch((err) => {
        return Promise.reject(err);
      });
    }

    (originalRequest as any)._retry = true;
    this.isRefreshing = true;

    try {
      const refreshResult = await this.refreshAccessToken();
      
      if (refreshResult.success && refreshResult.accessToken) {
        // Process queued requests
        this.processQueue(null, refreshResult.accessToken);
        
        // Retry original request with new token
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${refreshResult.accessToken}`;
        
        return axiosInstance(originalRequest);
      } else {
        this.processQueue(new Error('Token refresh failed'), null);
        this.redirectToLogin();
        return Promise.reject(new Error('Token refresh failed'));
      }
    } catch (error) {
      console.error('[TOKEN_REFRESH] Token refresh error:', error);
      this.processQueue(error, null);
      this.redirectToLogin();
      return Promise.reject(error);
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Refresh access token using refresh token
   */
  private async refreshAccessToken(): Promise<RefreshTokenResponse> {
    try {
      const refreshToken = this.getRefreshTokenFromStorage();
      
      if (!refreshToken) {
        return {
          success: false,
          message: 'No refresh token available'
        };
      }

      // Check if refresh token is expired
      if (this.isRefreshTokenExpired(refreshToken)) {
        this.clearTokens();
        return {
          success: false,
          message: 'Refresh token expired'
        };
      }

      // Call refresh endpoint
      const response = await axios.post(
        `${config.apiUrl}/auth/refresh`,
        { refreshToken },
        { timeout: 15000 }
      );

      if (response.data.success && response.data.accessToken) {
        // Update tokens in storage
        this.setAccessToken(response.data.accessToken);
        
        if (response.data.refreshToken) {
          this.setRefreshToken(response.data.refreshToken);
        }


        return {
          success: true,
          accessToken: response.data.accessToken,
          refreshToken: response.data.refreshToken,
          expiresIn: response.data.expiresIn
        };
      }

      return {
        success: false,
        message: 'Invalid response from refresh endpoint'
      };

    } catch (error: any) {
      console.error('[TOKEN_REFRESH] Refresh token API error:', error);
      
      if (error.response?.status === 401) {
        // Refresh token is invalid, clear all tokens
        this.clearTokens();
        return {
          success: false,
          message: 'Refresh token is invalid'
        };
      }

      return {
        success: false,
        message: error.response?.data?.message || 'Token refresh failed'
      };
    }
  }

  /**
   * Process queued requests after token refresh
   */
  private processQueue(error: any, token: string | null): void {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });
    
    this.failedQueue = [];
  }





  /**
   * Set access token in storage
   */
  private setAccessToken(token: string): void {
    // Calculate expiration time (default to 1 hour if not provided)
    const expirationHours = 1;
    Cookies.set('access_token', token, {
      expires: expirationHours,
      secure: true,
      sameSite: 'strict'
    });
  }

  /**
   * Set refresh token in storage
   */
  private setRefreshToken(token: string): void {
    Cookies.set('refresh_token', token, {
      expires: 7, // 7 days
      secure: true,
      sameSite: 'strict'
    });
  }


  /**
   * Redirect to login page
   */
  private redirectToLogin(): void {
    const currentPath = window.location.pathname;
    window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
  }


  /**
   * Manual refresh token call (for testing or manual refresh)
   */
  public async manualRefreshToken(): Promise<RefreshTokenResponse> {
    return await this.refreshAccessToken();
  }

  /**
   * Get refresh token from storage (public method)
   */
  public getRefreshTokenFromStorage(): string | null {
    return Cookies.get('refresh_token') || null;
  }

  /**
   * Check if refresh token is expired (public method)
   */
  public isRefreshTokenExpired(token: string): boolean {
    try {
      // Decode JWT without verification to check expiry
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
      
      const decoded = JSON.parse(jsonPayload);
      if (!decoded || !decoded.exp) return true;
      
      const currentTime = Math.floor(Date.now() / 1000);
      return decoded.exp < currentTime;
    } catch {
      return true;
    }
  }

  /**
   * Clear all tokens (public method)
   */
  public clearTokens(): void {
    Cookies.remove('access_token');
    Cookies.remove('refresh_token');
  }
}

// Export singleton instance
export const tokenRefreshService = new TokenRefreshService();
